import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  Button,
  Alert,
  InputLabel,
  FormControl,
  Snackbar,
  IconButton,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const TRIGGER_OPTIONS = [
  { value: 'budget_percentage', label: 'Budget Percentage (Monthly)' },
  { value: 'daily_summary', label: 'Daily Summary' },
  { value: 'weekly_summary', label: 'Weekly Summary' },
  { value: 'transaction', label: 'Transaction Alert' },
];

const AdminPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    trigger: 'budget_percentage',
    threshold: 80,
    message: 'You\'ve spent {amount} ({percentage}% of your budget)',
    variables: ['amount', 'percentage', 'budgetLimit'],
    active: true
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/admin/notification-templates');
      setTemplates(response.data);
      setError('');
    } catch (error) {
      let details = error.response?.data?.error || error.message;
      if (error.response?.status === 403) {
        details = 'You do not have admin access. Please log in as an admin.';
        navigate('/');
      }
      setError('Failed to load templates: ' + details);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/admin/notification-template', newTemplate);
      setSuccess('Template created successfully');
      fetchTemplates();
      setNewTemplate({
        name: '',
        trigger: 'budget_percentage',
        threshold: 80,
        message: 'You\'ve spent {amount} ({percentage}% of your budget)',
        variables: ['amount', 'percentage', 'budgetLimit'],
        active: true
      });
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create template');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await axiosInstance.delete(`/admin/notification-template/${id}`);
      setSuccess('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete template');
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await axiosInstance.patch(`/admin/notification-template/${id}/toggle`);
      setSuccess(`Template ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchTemplates();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to toggle template');
    }
  };

  const handleTestNotification = async (templateId) => {
    try {
      await axiosInstance.post(`/admin/notification-template/${templateId}/test`);
      setSuccess('Test notification sent successfully');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to send test notification');
    }
  };

  const handleSimpleTestNotification = async () => {
    try {
      await axiosInstance.post('/admin/simple-test-notification');
      setSuccess('Simple test notification sent successfully');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to send simple test notification');
    }
  };

  const handleSettingsStyleTest = async () => {
    try {
      await axiosInstance.post('/push/test');
      setSuccess('Settings-style test notification sent successfully');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to send settings-style test notification');
    }
  };

  const handleHardcodedTest = async () => {
    try {
      await axiosInstance.post('/admin/hardcoded-test');
      setSuccess('Hardcoded test notification sent successfully');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to send hardcoded test notification');
    }
  };

  const handleTrustedPathTest = async (templateId) => {
    try {
      await axiosInstance.post(`/push/template-test/${templateId}`);
      setSuccess('Template notification sent through trusted path successfully');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to send through trusted path');
    }
  };

  const handleCloseSnackbar = () => {
    setError('');
    setSuccess('');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#e5e7eb', py: 6 }}>
      <Grid container justifyContent="center">
        <Grid item xs={12} md={10} lg={8}>
          <Card elevation={6} sx={{ borderRadius: 3, bgcolor: '#fff' }}>
            <CardContent>
              <Box display="flex" flexDirection="column" gap={2} mb={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h4" fontWeight={700} color="#222">
                    Notification Templates
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={() => navigate('/')} 
                    sx={{ color: '#222', borderColor: '#222', '&:hover': { borderColor: '#1976d2', color: '#1976d2' } }}
                  >
                    Back to Dashboard
                  </Button>
                </Box>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSimpleTestNotification} 
                    sx={{ fontWeight: 600 }}
                  >
                    Send Simple Test (Admin)
                  </Button>
                  <Button 
                    variant="contained" 
                    color="secondary" 
                    onClick={handleSettingsStyleTest} 
                    sx={{ fontWeight: 600 }}
                  >
                    Send Settings-Style Test
                  </Button>
                  <Button 
                    variant="contained" 
                    color="success" 
                    onClick={handleHardcodedTest} 
                    sx={{ fontWeight: 600 }}
                  >
                    Send Hardcoded Test
                  </Button>
                </Box>
              </Box>
              <Divider sx={{ mb: 3 }} />
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
              )}
              <Typography variant="h6" fontWeight={600} gutterBottom color="#222">
                Create New Template
              </Typography>
              <Box component="form" onSubmit={handleSubmit} mb={4}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Template Name"
                      value={newTemplate.name}
                      onChange={e => setNewTemplate({ ...newTemplate, name: e.target.value })}
                      fullWidth
                      required
                      InputLabelProps={{ style: { color: '#333' } }}
                      InputProps={{ style: { color: '#222', background: '#fafafa' } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel style={{ color: '#333' }}>Trigger Type</InputLabel>
                      <Select
                        label="Trigger Type"
                        value={newTemplate.trigger}
                        onChange={e => setNewTemplate({ ...newTemplate, trigger: e.target.value })}
                        sx={{ color: '#222', background: '#fafafa', '.MuiSelect-select': { color: '#222' } }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              bgcolor: '#fff',
                              color: '#222',
                            },
                          },
                        }}
                      >
                        {TRIGGER_OPTIONS.map(option => (
                          <MenuItem key={option.value} value={option.value} style={{ color: '#222', background: '#fff' }}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {newTemplate.trigger === 'budget_percentage' && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Threshold (%)"
                        type="number"
                        value={newTemplate.threshold}
                        onChange={e => setNewTemplate({ ...newTemplate, threshold: e.target.value })}
                        fullWidth
                        required
                        inputProps={{ min: 0, max: 100 }}
                        InputLabelProps={{ style: { color: '#333' } }}
                        InputProps={{ style: { color: '#222', background: '#fafafa' } }}
                        helperText="This is the percentage of your monthly budget."
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      label="Message Template"
                      value={newTemplate.message}
                      onChange={e => setNewTemplate({ ...newTemplate, message: e.target.value })}
                      fullWidth
                      multiline
                      minRows={2}
                      required
                      helperText="Available variables: {amount}, {percentage}, {budgetLimit}, {topCategories}"
                      InputLabelProps={{ style: { color: '#333' } }}
                      InputProps={{ style: { color: '#222', background: '#fafafa' } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button type="submit" variant="contained" color="primary" fullWidth sx={{ fontWeight: 700, fontSize: 18, py: 1.5 }}>
                      Create Template
                    </Button>
                  </Grid>
                </Grid>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="h6" fontWeight={600} gutterBottom color="#222">
                Existing Templates
              </Typography>
              {loading ? (
                <Typography>Loading...</Typography>
              ) : templates.length === 0 ? (
                <Typography color="text.secondary">No templates found</Typography>
              ) : (
                <Grid container spacing={2}>
                  {templates.map(template => (
                    <Grid item xs={12} key={template._id}>
                      <Card variant="outlined" sx={{ mb: 2, bgcolor: '#fafafa', borderColor: '#1976d2' }}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="subtitle1" fontWeight={600} color="#222">{template.name}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Trigger: {TRIGGER_OPTIONS.find(opt => opt.value === template.trigger)?.label || template.trigger}
                                {template.trigger === 'budget_percentage' && ` | Threshold: ${template.threshold}%`}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace', bgcolor: '#f7f7f7', p: 1, borderRadius: 1, color: '#222' }}>
                                {template.message}
                              </Typography>
                            </Box>
                            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                              <Button size="small" variant="outlined" color="info" onClick={() => handleTestNotification(template._id)} sx={{ color: '#1976d2', borderColor: '#1976d2' }}>
                                Test
                              </Button>
                              <Button size="small" variant="outlined" color="success" onClick={() => handleTrustedPathTest(template._id)} sx={{ fontWeight: 600 }}>
                                Test (Trusted Path)
                              </Button>
                              <Button size="small" variant="outlined" color={template.active ? 'warning' : 'success'} onClick={() => handleToggle(template._id, template.active)} sx={{ fontWeight: 600 }}>
                                {template.active ? 'Deactivate' : 'Activate'}
                              </Button>
                              <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(template._id)} sx={{ fontWeight: 600 }}>
                                Delete
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Snackbar
        open={!!error || !!success}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={error ? 'error' : 'success'}
          sx={{ width: '100%' }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleCloseSnackbar}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {error || success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPage; 